import { motion } from "framer-motion";
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = [
  "MCA (Merchant Cash Advance)",
  "Term Loan",
  "Line of Credit",
  "SBA Loans",
  "Equipment Financing",
  "Bridge Funding",
  "Real Estate Financing",
  "0% Interest Business Credit Cards",
  "Startup Funding",
  "Consolidation",
  "Personal Loans",
  "Credit Repair Services",
];

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.status === "success") {
        toast.success(response.data.message);
        setFormData({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
      }
    } catch (error) {
      console.error("Contact form error:", error);
      toast.error("Something went wrong. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 px-6 md:px-12 relative" data-testid="contact-section">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="font-playfair text-4xl md:text-6xl font-bold mb-4">
            <span className="gold-gradient-text" data-testid="contact-heading">
              Get Started Today
            </span>
          </h2>
          <p className="font-manrope text-white/70 text-lg">
            Fill out the form below and we'll get back to you within 24 hours
          </p>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="glass-card p-8 md:p-12 rounded-2xl border-2 border-gold/20"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name */}
            <div>
              <label
                htmlFor="name"
                className="block font-manrope text-white/90 mb-2 text-sm font-medium"
              >
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/20 text-white placeholder:text-white/30 transition-all duration-300"
                placeholder="John Doe"
                data-testid="contact-name-input"
              />
            </div>

            {/* Email */}
            <div>
              <label
                htmlFor="email"
                className="block font-manrope text-white/90 mb-2 text-sm font-medium"
              >
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/20 text-white placeholder:text-white/30 transition-all duration-300"
                placeholder="john@example.com"
                data-testid="contact-email-input"
              />
            </div>

            {/* Phone */}
            <div>
              <label
                htmlFor="phone"
                className="block font-manrope text-white/90 mb-2 text-sm font-medium"
              >
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/20 text-white placeholder:text-white/30 transition-all duration-300"
                placeholder="(555) 123-4567"
                data-testid="contact-phone-input"
              />
            </div>

            {/* Service */}
            <div>
              <label
                htmlFor="service"
                className="block font-manrope text-white/90 mb-2 text-sm font-medium"
              >
                Service Interested In *
              </label>
              <select
                id="service"
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/20 text-white transition-all duration-300"
                data-testid="contact-service-select"
              >
                <option value="" className="bg-[#0A0A0A] text-white">
                  Select a service...
                </option>
                {serviceOptions.map((service, index) => (
                  <option
                    key={index}
                    value={service}
                    className="bg-[#0A0A0A] text-white"
                  >
                    {service}
                  </option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="message"
                className="block font-manrope text-white/90 mb-2 text-sm font-medium"
              >
                Message *
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg focus:border-gold/50 focus:outline-none focus:ring-2 focus:ring-gold/20 text-white placeholder:text-white/30 transition-all duration-300 resize-none"
                placeholder="Tell us about your funding needs..."
                data-testid="contact-message-textarea"
              />
            </div>

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full px-8 py-4 bg-gold hover:bg-gold-600 disabled:bg-gold/50 text-black font-manrope font-semibold text-lg rounded-lg transition-all duration-300"
              data-testid="contact-submit-button"
            >
              {isSubmitting ? "Sending..." : "Submit Request"}
            </motion.button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}